/**
 * MapControlHint — animated tooltip card for a map control button.
 *
 * Rendered inside a `relative` positioned wrapper div (the button's container).
 * Floats above the button, right-aligned, with a downward arrow.
 *
 * Dismisses on:
 *  - X button click / touchEnd
 *  - pointerdown anywhere outside the card
 *  - after 8 seconds automatically
 *
 * @param {{ visible, onDismiss, title, description }} props
 * @atomic @idempotent
 */
import { memo, useEffect, useRef, useCallback } from 'react'
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

const MapControlHint = memo(function MapControlHint({
  visible,
  onDismiss,
  title,
  description,
}) {
  const cardRef = useRef(null)

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!visible) return
    const t = setTimeout(onDismiss, 8000)
    return () => clearTimeout(t)
  }, [visible, onDismiss])

  // Dismiss on pointerdown outside the card
  useEffect(() => {
    if (!visible) return
    const onOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onDismiss()
    }
    // Small delay so the triggering tap doesn't immediately dismiss
    const id = setTimeout(() => document.addEventListener('pointerdown', onOutside), 50)
    return () => {
      clearTimeout(id)
      document.removeEventListener('pointerdown', onOutside)
    }
  }, [visible, onDismiss])

  const stopProp = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  if (!visible) return null

  return (
    <div
      ref={cardRef}
      className="absolute bottom-full right-0 mb-3 z-[1100] w-56 map-hint-card"
      onClick={stopProp}
      onTouchEnd={stopProp}
      role="tooltip"
    >
      {/* Card body */}
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-3">
        {/* Header */}
        <div className="flex items-start gap-1.5 mb-1.5">
          <InformationCircleIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="flex-1 text-xs font-semibold text-gray-900 leading-snug">{title}</p>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss() }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss() }}
            className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 transition-colors"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      </div>

      {/* Downward arrow — points at the button below */}
      <div
        className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45"
        aria-hidden="true"
      />
    </div>
  )
})

export default MapControlHint
